import { LogLevel } from "@azure/msal-browser";

// B2C Configuration (for participants)
export const b2cConfig = {
  tenantName: "relmed",
  clientId: "2bf86a50-1598-4a57-9c3c-f736585b94f2",
  // Basic sign-in flow names
  signUpSignInFlowName: "B2C_1_signin_email", // Default flow - now email
  signInEmailFlowName: "B2C_1_signin_email",  // Email specific flow
  signInPhoneFlowName: "B2C_1_signin_phone",  // Phone specific flow
  signInMicrosoftFlowName: "B2C_1_signin_microsoft",
  signInGoogleFlowName: "B2C_1_signin_google",
  // Signup flow names
  signUpFlowName: "B2C_1_signup_email", // Default signup flow - now email
  signUpEmailFlowName: "B2C_1_signup_email",
  signUpPhoneFlowName: "B2C_1_signup_phone",
  signUpMicrosoftFlowName: "B2C_1_signup_microsoft",
  signUpGoogleFlowName: "B2C_1_signup_google",
  // Password reset flow
  forgotPasswordFlowName: "B2C_1_passwordreset",
};

// Helper to get B2C authority URL for a specific user flow
const getB2CAuthority = (userFlow) =>
  `https://${b2cConfig.tenantName}.b2clogin.com/${b2cConfig.tenantName}.onmicrosoft.com/${userFlow}`;

// MSAL config for B2C (participants)
export const b2cMsalConfig = {
  auth: {
    clientId: b2cConfig.clientId,
    authority: getB2CAuthority(b2cConfig.signUpSignInFlowName),
    knownAuthorities: [`${b2cConfig.tenantName}.b2clogin.com`],
    // Dynamically set redirectUri based on current origin
    redirectUri: typeof window !== 'undefined' 
      ? `${window.location.origin}/participant` 
      : (process.env.NODE_ENV === "production" ? "https://relmed.ac.uk/participant" : "http://localhost:3000/participant"), // Fallback for non-browser environments if needed
    navigateToLoginRequestUrl: false,
    usePkce: true
  },
  cache: {
    cacheLocation: "localStorage", // B2C users (participants) use localStorage for persistent login across browser sessions
    storeAuthStateInCookie: false, // No need to store in cookies as modern browsers fully support localStorage
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  }
};

// MSAL config for Entra ID (admin/PI)
export const msalConfig = {
  auth: {
    clientId: "76aa34c3-0e31-4f5b-be1b-ee769da13dd2", // Relmed app registration client ID
    authority:
      "https://login.microsoftonline.com/fd9e5fa6-2eeb-4605-ba36-4e56ef453b7c", // UCL tenant ID
    // Dynamically set redirectUri based on current origin
    redirectUri: typeof window !== 'undefined' 
      ? `${window.location.origin}/admin` 
      : (process.env.NODE_ENV === "production" ? "https://relmed.ac.uk/admin" : "http://localhost:3000/admin"), // Fallback for non-browser environments if needed
    usePkce: true,
  },
  cache: {
    cacheLocation: "sessionStorage", // Entra ID users (admins/PIs) use sessionStorage for session-only login that clears on browser close
    storeAuthStateInCookie: false, // No need to store in cookies as modern browsers fully support sessionStorage
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

// Define API scopes for the Azure Function app
export const apiScopes = {
  // Use the exact audience format expected by the server for B2C
  participantScope: "https://relmed.onmicrosoft.com/relmedapi/Relmed.PPT", // This sets audience to API app ID
  
  // For Entra ID, use the URI format required for the API audience validation
  adminScope: "api://relmedapi/Relmed.User", // This sets audience to api://relmedapi
  
  // Include documentation about why these formats are crucial
  // The audience value in the resulting token MUST match what the server expects:
  // - For B2C: The audience is set to the actual application ID
  // - For Entra ID: The audience is set to the API URI identifier
  
  // Debug info - for troubleshooting
  debugInfo: () => {
    console.group("Azure AD B2C API Scope Debugging");
    console.log("Current participantScope:", apiScopes.participantScope);
    console.log("Current adminScope:", apiScopes.adminScope);
    console.log("B2C Client ID:", b2cConfig.clientId);
    console.log("B2C Tenant:", b2cConfig.tenantName);
    console.log("B2C Authority:", getB2CAuthority(b2cConfig.signUpSignInFlowName));
    console.groupEnd();
  }
};

export const b2cLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInEmailFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "login",
  state: "b2c",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_email",
  }
};

// Registration request for B2C with Email only
export const b2cEmailRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpEmailFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_email_registration",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signup_email"
  }
};

// Registration request for B2C with Microsoft
export const b2cMicrosoftRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpMicrosoftFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_microsoft_registration",
  extraQueryParameters: {
    p: "B2C_1_signup_microsoft"
  }
};

// Registration request for B2C with Google
export const b2cGoogleRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpGoogleFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_google_registration",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signup_google"
  }
};

// Email login request
export const b2cEmailLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInEmailFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "login",
  state: "b2c_email",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_email"
  }
};

// Phone login request
export const b2cPhoneLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInPhoneFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "login",
  state: "b2c_phone",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_phone"
  }
};

// Login request for B2C with Microsoft
export const b2cMicrosoftLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInMicrosoftFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "select_account",
  state: "b2c_microsoft",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_microsoft"
  }
};

// Login request for B2C with Google
export const b2cGoogleLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInGoogleFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "select_account",
  state: "b2c_google",
  extraQueryParameters: {
    response_mode: "fragment",
    domain_hint: "google.com",
    p: "B2C_1_signin_google"
  }
};

// Login request for Entra ID
export const loginRequest = {
  scopes: ["openid", "offline_access", apiScopes.adminScope],
  prompt: "select_account",
  state: "entra",
  extraQueryParameters: {
    response_mode: "fragment",
  }
};

// Password reset config for B2C
export const b2cPasswordResetRequest = {
  authority: getB2CAuthority(b2cConfig.forgotPasswordFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_password_reset",
};

// Registration request for B2C with Phone
export const b2cPhoneRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpPhoneFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_phone_registration",
  extraQueryParameters: {
    p: "B2C_1_signup_phone",
    response_mode: "fragment",
  }
};

// Graph API config (for admin/PI operations)
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};
